import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, SectionLabel, StatBlock } from "@/components/Primitives";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import {
  Loader2, RefreshCw, ArrowRight, Activity, Trophy, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function fmt(n) { return (n ?? 0).toLocaleString("en-US"); }
function fmtMoney(n, max = 0) {
  if (n == null) return "—";
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: max })}`;
}
function shortDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const INTEGRATION_LABEL = {
  audience_source: "Audience Source",
  campaign_push: "Campaign Push",
  report_sync: "Report Sync",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/dashboard/overview");
    setData(data);
  };

  useEffect(() => {
    (async () => { try { await load(); } finally { setLoading(false); } })();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };

  if (loading) {
    return (
      <div className="px-8 md:px-12 py-10">
        <p className="text-sm text-[#555] flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading dashboard…
        </p>
      </div>
    );
  }
  if (!data) return null;

  const { counters, aggregate_metrics, top_advertisers, top_campaigns, recent_queries, recent_campaigns, recent_logs, timeseries_7d, integration_health } = data;

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1500px]">
      <PageHeader
        eyebrow={`Welcome, ${user?.name?.split(" ")[0] || "operator"}`}
        title="Dashboard"
        description="A single view of activity across audience queries, campaign activations, and reporting — refreshed every time you land here."
        testid="dashboard-header"
        actions={
          <button
            onClick={refresh}
            disabled={refreshing}
            data-testid="dashboard-refresh-button"
            className="border border-[#0A0A0A] px-4 py-2 text-xs font-mono uppercase tracking-wider flex items-center gap-2 hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-60"
          >
            {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        }
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
        <StatBlock label="Audience queries" value={fmt(counters.queries)} testid="kpi-queries" />
        <StatBlock label="Advertisers" value={fmt(counters.advertisers)} testid="kpi-advertisers" />
        <StatBlock label="Campaigns" value={`${fmt(counters.accepted_campaigns)} / ${fmt(counters.campaigns)}`} sublabel="accepted / total" testid="kpi-campaigns" />
        <StatBlock label="Impressions" value={fmt(aggregate_metrics.impressions)} testid="kpi-impressions" />
        <StatBlock label="Spend" value={fmtMoney(aggregate_metrics.spend_usd)} sublabel={`of ${fmtMoney(aggregate_metrics.total_budget_usd)} budget`} testid="kpi-spend" />
        <StatBlock label="Conversions" value={fmt(aggregate_metrics.conversions)} sublabel={`CTR ${aggregate_metrics.avg_ctr}%`} testid="kpi-conversions" />
      </div>

      {/* Chart + integration health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 border border-[#E0E0D8] bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <SectionLabel>Activity · last 7 days</SectionLabel>
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#888]">queries · campaigns</p>
          </div>
          <div style={{ width: "100%", height: 220 }} data-testid="dashboard-chart">
            <ResponsiveContainer>
              <BarChart data={timeseries_7d} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="#E0E0D8" strokeDasharray="2 4" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={shortDate}
                  tick={{ fill: "#555", fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  axisLine={{ stroke: "#E0E0D8" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#555", fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  axisLine={{ stroke: "#E0E0D8" }}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "#F4F4F0" }}
                  contentStyle={{
                    background: "#0A0A0A", color: "#fff", border: "none",
                    borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12,
                  }}
                  labelFormatter={shortDate}
                />
                <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }} />
                <Bar dataKey="queries" fill="#0A0A0A" />
                <Bar dataKey="campaigns" fill="#FF3E00" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-[#E0E0D8] bg-white p-6">
          <SectionLabel>Integration health (last 50)</SectionLabel>
          <ul className="space-y-4 mt-3" data-testid="integration-health">
            {Object.entries(integration_health).map(([k, v]) => {
              const rate = v.success_rate;
              const tone =
                rate == null ? "text-[#888] border-[#E0E0D8]" :
                rate >= 95   ? "text-[#00994D] border-[#00994D]/30 bg-[#00994D]/5" :
                rate >= 80   ? "text-[#FF3E00] border-[#FF3E00]/30 bg-[#FF3E00]/5" :
                               "text-[#D91919] border-[#D91919]/30 bg-[#D91919]/5";
              return (
                <li key={k} className="flex items-center justify-between" data-testid={`health-${k}`}>
                  <div>
                    <p className="text-sm font-medium">{INTEGRATION_LABEL[k]}</p>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-[#555] mt-0.5">
                      {v.count} call{v.count === 1 ? "" : "s"}
                    </p>
                  </div>
                  <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 border ${tone}`}>
                    {rate == null ? "no data" : `${rate}%`}
                  </span>
                </li>
              );
            })}
          </ul>
          <Link to="/logs" className="mt-5 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-[#FF3E00] hover:underline">
            View all integration logs <ArrowRight size={11} />
          </Link>
        </div>
      </div>

      {/* Top advertisers + top campaigns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        <div className="border border-[#E0E0D8] bg-white">
          <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-[#E0E0D8]">
            <SectionLabel>Top advertisers · by spend</SectionLabel>
            <Trophy size={14} className="text-[#FF3E00]" />
          </div>
          {top_advertisers.length === 0 ? (
            <p className="px-6 py-8 text-sm text-[#888] font-mono">No advertiser performance yet.</p>
          ) : (
            <table className="w-full border-collapse" data-testid="top-advertisers">
              <tbody>
                {top_advertisers.map((a, i) => (
                  <tr key={a.advertiser_id} className="border-b border-[#E0E0D8] last:border-b-0">
                    <td className="py-3 px-6 w-10 font-mono text-xs text-[#888]">{String(i + 1).padStart(2, "0")}</td>
                    <td className="py-3 px-4 text-sm font-medium">{a.name}</td>
                    <td className="py-3 px-4 text-xs font-mono text-[#555] text-right">
                      {fmt(a.campaigns)} camp · {fmt(a.conversions)} conv
                    </td>
                    <td className="py-3 px-6 text-right font-mono text-sm">{fmtMoney(a.spend_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border border-[#E0E0D8] bg-white">
          <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-[#E0E0D8]">
            <SectionLabel>Top campaigns · by conversions</SectionLabel>
            <Activity size={14} className="text-[#FF3E00]" />
          </div>
          {top_campaigns.length === 0 ? (
            <p className="px-6 py-8 text-sm text-[#888] font-mono">No campaign performance yet.</p>
          ) : (
            <table className="w-full border-collapse" data-testid="top-campaigns">
              <tbody>
                {top_campaigns.map((c, i) => (
                  <tr key={c.id} className="border-b border-[#E0E0D8] last:border-b-0 hover:bg-[#F4F4F0]">
                    <td className="py-3 px-6 w-10 font-mono text-xs text-[#888]">{String(i + 1).padStart(2, "0")}</td>
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium truncate max-w-[260px]">{c.name}</p>
                      <p className="text-[10px] font-mono uppercase tracking-wider text-[#555] mt-0.5">
                        {c.advertiser || "—"} · CTR {c.ctr}%
                      </p>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-sm">{fmt(c.conversions)}</td>
                    <td className="py-3 px-6 text-right">
                      <Link to={`/campaigns/${c.id}`} className="text-[11px] font-mono uppercase tracking-wider text-[#FF3E00] inline-flex items-center gap-1 hover:underline">
                        Open <ArrowRight size={11} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <RecentList
          title="Recent queries"
          testid="recent-queries"
          empty="No queries yet."
          link={{ to: "/history", label: "All queries" }}
          items={recent_queries.map((q) => ({
            id: q.id,
            primary: q.label || `${q.latitude}, ${q.longitude}`,
            secondary: `${q.latitude.toFixed(3)}, ${q.longitude.toFixed(3)}`,
            meta: q.status,
            when: q.created_at,
            tone: q.status === "completed" ? "ok" : q.status === "failed" ? "err" : "neutral",
          }))}
        />
        <RecentList
          title="Recent campaigns"
          testid="recent-campaigns"
          empty="No campaigns yet."
          link={{ to: "/campaigns", label: "All campaigns" }}
          items={recent_campaigns.map((c) => ({
            id: c.id,
            primary: c.name,
            secondary: c.advertiser || "—",
            meta: c.push_status,
            when: c.created_at,
            tone: c.push_status === "accepted" ? "ok" : "err",
            link: `/campaigns/${c.id}`,
          }))}
        />
        <RecentList
          title="Recent integration calls"
          testid="recent-logs"
          empty="No logs yet."
          link={{ to: "/logs", label: "All logs" }}
          items={recent_logs.map((l) => ({
            id: l.id,
            primary: INTEGRATION_LABEL[l.integration] || l.integration,
            secondary: l.endpoint,
            meta: `${l.latency_ms} ms`,
            when: l.created_at,
            tone: l.success ? "ok" : "err",
          }))}
        />
      </div>
    </div>
  );
}

function RecentList({ title, items, empty, link, testid }) {
  return (
    <div className="border border-[#E0E0D8] bg-white">
      <div className="px-5 pt-4 pb-3 border-b border-[#E0E0D8] flex items-center justify-between">
        <SectionLabel>{title}</SectionLabel>
        {link && (
          <Link to={link.to} className="text-[10px] font-mono uppercase tracking-wider text-[#FF3E00] hover:underline">
            {link.label} →
          </Link>
        )}
      </div>
      {items.length === 0 ? (
        <p className="px-5 py-8 text-xs font-mono text-[#888]">{empty}</p>
      ) : (
        <ul data-testid={testid}>
          {items.map((it) => (
            <li key={it.id} className="px-5 py-3 border-b border-[#E0E0D8] last:border-b-0 hover:bg-[#F4F4F0]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {it.link ? (
                    <Link to={it.link} className="text-sm font-medium hover:underline truncate block">{it.primary}</Link>
                  ) : (
                    <p className="text-sm font-medium truncate">{it.primary}</p>
                  )}
                  <p className="text-[10px] font-mono uppercase tracking-wider text-[#555] mt-0.5 truncate">
                    {it.secondary}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${
                    it.tone === "ok"  ? "text-[#00994D] border-[#00994D]/30 bg-[#00994D]/10" :
                    it.tone === "err" ? "text-[#D91919] border-[#D91919]/30 bg-[#D91919]/10" :
                                         "text-[#555] border-[#E0E0D8] bg-[#F4F4F0]"
                  }`}>
                    {it.tone === "ok" ? <CheckCircle2 size={9} /> : it.tone === "err" ? <AlertTriangle size={9} /> : null}
                    {it.meta}
                  </span>
                  <p className="text-[10px] font-mono text-[#888] mt-1">
                    {new Date(it.when).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
